import { useId, useState } from 'react';
import { Link } from 'react-router';
import { clsx } from 'clsx';
import { SEO, BreadcrumbSchema, ArticleSchema, FAQSchema, HowToSchema } from '@/components/seo/seo';
import { CouncilDataCallout } from './council-data-callout';

interface FAQ {
  question: string;
  answer: string;
}

interface HowToData {
  name: string;
  description: string;
  steps: { name: string; text: string }[];
}

interface GuideLayoutProps {
  slug: string;
  category: string;
  categoryColor: 'brand' | 'accent' | 'success' | 'warning';
  title: string;
  seoTitle?: string;
  description: string;
  readTime: string;
  updatedDate: string;
  reviewedDate?: string;
  datePublished?: string;
  reviewedFor?: string;
  contentOwner?: string;
  correctionHref?: string;
  sources?: { name: string; url: string }[];
  children: React.ReactNode;
  faqs?: FAQ[];
  /** Pass for guides with a step-by-step section — emits HowTo JSON-LD */
  howTo?: HowToData;
  /** Key facts rendered as an "At a Glance" box under the title — a summary block for LLM crawlers */
  atAGlance?: { label: string; value: string }[];
  showCouncilDataCallout?: boolean;
  relatedGuides?: {
    slug: string;
    title: string;
    readTime: string;
  }[];
}

const categoryGradients = {
  brand: 'from-brand-600 to-brand-700',
  accent: 'from-accent-500 to-accent-600',
  success: 'from-success-500 to-success-600',
  warning: 'from-warning-500 to-warning-600',
};

const categoryIcons = {
  brand: (
    <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  accent: (
    <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  success: (
    <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
    </svg>
  ),
  warning: (
    <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
};

export function GuideLayout({ 
  slug,
  category: _category, 
  categoryColor, 
  title, 
  seoTitle,
  description, 
  readTime,
  updatedDate,
  reviewedDate,
  datePublished = '2026-01-10',
  reviewedFor = 'UK law',
  contentOwner,
  correctionHref = 'mailto:hello@fixtyer.co.uk?subject=Factual%20correction',
  sources = [],
  children,
  faqs = [],
  howTo,
  atAGlance = [],
  showCouncilDataCallout = false,
  relatedGuides = []
}: GuideLayoutProps) {
  // _category is available for future use (e.g., category badges)
  void _category;
  
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const faqIdPrefix = useId();

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Guides', url: '/guides' },
    { name: title, url: `/guides/${slug}` },
  ];

  // Convert updated date to ISO format for schema
  const getIsoDate = (dateStr: string): string => {
    if (dateStr.includes('-')) return dateStr;
    try {
      const hasYear = /\b\d{4}\b/.test(dateStr);
      const parsed = new Date(Date.parse(
        dateStr.replace(/(\d+)(st|nd|rd|th)/, '$1') + (hasYear ? '' : ' 2026')
      ));
      const isoString = parsed.toISOString();
      const datePart = isoString.split('T')[0];
      return datePart ?? '2026-01-10';
    } catch {
      return '2026-01-10'; // fallback
    }
  };
  const formatVisibleDate = (date: string) => /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(`${date}T00:00:00Z`))
    : date;
  // dateModified is the later of the content update and the last review, so an
  // update after a review is not hidden behind the older review date.
  const isoModifiedDate = [updatedDate, reviewedDate]
    .filter((value): value is string => Boolean(value))
    .map(getIsoDate)
    .sort()
    .at(-1) ?? getIsoDate(updatedDate);
  const visibleUpdatedDate = formatVisibleDate(updatedDate);
  const visiblePublishedDate = formatVisibleDate(datePublished);
  const visibleReviewedDate = reviewedDate ? formatVisibleDate(reviewedDate) : null;

  return (
    <div className="relative">
      <SEO
        title={seoTitle ?? `${title} | Fixtyer`}
        description={description}
        canonical={`/guides/${slug}`}
        type="article"
        article={{
          publishedTime: datePublished,
          modifiedTime: isoModifiedDate,
          author: contentOwner ?? 'Fixtyer Team',
        }}
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <ArticleSchema
        title={title}
        description={description}
        url={`/guides/${slug}`}
        datePublished={datePublished}
        dateModified={isoModifiedDate}
        author={contentOwner ?? 'Fixtyer Team'}
      />
      {faqs.length > 0 && <FAQSchema faqs={faqs} />}
      {howTo && <HowToSchema name={howTo.name} description={howTo.description} steps={howTo.steps} />}
      
      {/* Breadcrumb */}
      <div className="bg-white border-b border-surface-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-brand-600">
            <Link to="/guides" className="hover:text-brand-700 transition-colors">Guides</Link>
            <svg className="w-4 h-4 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-600 font-medium">{title}</span>
          </div>
        </div>
      </div>

      {/* Hero Header */}
      <section className={`relative bg-gradient-to-r ${categoryGradients[categoryColor]} py-10 pb-14`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
              {categoryIcons[categoryColor]}
            </div>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-2">
                {title}
              </h1>
              <p className="text-white/90 text-lg max-w-2xl">
                {description}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom diagonal stripe bar */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* Meta Info Row */}
      <section className="bg-white border-b border-surface-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            {/* Read time */}
            <div className="flex items-center gap-1.5 text-surface-600">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {readTime} read
            </div>

            {/* Updated date */}
            {contentOwner && (
              <div className="flex items-center gap-1.5 text-surface-600">
                Published: {visiblePublishedDate}
              </div>
            )}

            <div className="flex items-center gap-1.5 text-surface-600">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Updated {visibleUpdatedDate}
            </div>

            {visibleReviewedDate && (
              <div className="flex items-center gap-1.5 text-surface-600">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Last reviewed: {visibleReviewedDate}
              </div>
            )}

            {/* Reviewed badge */}
            <div className="flex items-center gap-1.5 text-success-600">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Reviewed for {reviewedFor}
            </div>

            {contentOwner && (
              <div className="flex items-center gap-1.5 text-surface-600">
                Content owner: {contentOwner}
              </div>
            )}

            {/* Sources */}
            {sources.length > 0 && (
              <div className="flex items-center gap-2 text-surface-500">
                <span>Sources:</span>
                {sources.map((source, i) => (
                  <a 
                    key={i}
                    href={source.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:text-brand-700 underline decoration-brand-300"
                  >
                    {source.name}
                    <svg className="w-3 h-3 inline ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                ))}
              </div>
            )}

            {contentOwner && (
              <a
                href={correctionHref}
                className="font-medium text-brand-600 underline decoration-brand-300"
              >
                Report a factual correction
              </a>
            )}
          </div>
        </div>
      </section>

      {/* At a Glance — summary block for readers and LLM crawlers */}
      {atAGlance.length > 0 && (
        <section className="bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <div data-speakable="summary" className="bg-brand-50 border border-brand-200 rounded-2xl p-6">
              <h2 className="font-display text-lg font-bold text-brand-950 mb-4">At a glance</h2>
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {atAGlance.map((fact) => (
                  <div key={fact.label} className="flex flex-col">
                    <dt className="text-sm font-semibold text-brand-700">{fact.label}</dt>
                    <dd className="text-surface-700">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="guide-content">
            {showCouncilDataCallout && <CouncilDataCallout />}
            {children}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      {faqs.length > 0 && (
        <section className="py-12 bg-surface-50 border-t border-surface-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl border border-surface-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    aria-expanded={openFaq === index}
                    aria-controls={`${faqIdPrefix}-faq-panel-${index}`}
                    className="w-full px-6 py-5 flex items-start justify-between text-left hover:bg-surface-50 transition-colors"
                  >
                    <h3 className="font-display font-semibold text-brand-950 pr-4">
                      {faq.question}
                    </h3>
                    <div className={clsx(
                      'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all mt-0.5',
                      openFaq === index ? 'bg-brand-600 rotate-180' : 'bg-surface-100'
                    )}>
                      <svg 
                        className={clsx('w-4 h-4 transition-colors', openFaq === index ? 'text-white' : 'text-surface-600')} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  <div
                    id={`${faqIdPrefix}-faq-panel-${index}`}
                    className={clsx(
                      'overflow-hidden transition-all duration-300',
                      openFaq === index ? 'max-h-96' : 'max-h-0'
                    )}
                  >
                    <div className="px-6 pb-5 text-surface-700 leading-relaxed border-t border-surface-100 pt-4">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Guides */}
      {relatedGuides.length > 0 && (
        <section className="py-12 bg-white border-t border-surface-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-xl font-bold text-brand-950 mb-6">
              Related Guides
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedGuides.map((guide) => (
                <Link
                  key={guide.slug}
                  to={`/guides/${guide.slug}`}
                  className="group block bg-surface-50 rounded-xl border border-surface-200 p-5 hover:border-brand-300 hover:shadow-md transition-all"
                >
                  <h3 className="font-display font-semibold text-brand-950 mb-1 group-hover:text-brand-700 transition-colors">
                    {guide.title}
                  </h3>
                  <span className="text-sm text-surface-600">{guide.readTime} read</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12 bg-brand-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-display text-2xl font-bold text-white mb-2">
                Ready to start your claim?
              </h2>
              <p className="text-brand-300">
                Check your eligibility for free in just 2 minutes.
              </p>
            </div>
            <Link
              to="/check"
              className="inline-flex items-center justify-center px-6 py-3 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              Start Free Check
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom stripe */}
      <div 
        className="h-6"
        style={{
          background: `repeating-linear-gradient(
            -45deg,
            #1e40af,
            #1e40af 40px,
            #f97316 40px,
            #f97316 80px
          )`
        }}
      />
    </div>
  );
}

// Reusable styled components for guide content
export function GuideSection({ 
  icon, 
  title, 
  children 
}: { 
  icon: React.ReactNode; 
  title: string; 
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-6 mb-8 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center text-brand-600">
          {icon}
        </div>
        <h2 className="font-display text-xl font-bold text-brand-950">{title}</h2>
      </div>
      <div className="text-surface-700 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

export function GuideTip({ 
  variant = 'info',
  title,
  children 
}: { 
  variant?: 'info' | 'warning' | 'success';
  title?: string;
  children: React.ReactNode;
}) {
  const variants = {
    info: 'bg-brand-50 border-brand-200 text-brand-800',
    warning: 'bg-warning-500/10 border-warning-500/30 text-warning-700',
    success: 'bg-success-500/10 border-success-500/30 text-success-700',
  };

  const icons = {
    info: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    success: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  return (
    <div className={`rounded-xl border p-4 mb-6 ${variants[variant]}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{icons[variant]}</div>
        <div>
          {title && <p className="font-semibold mb-1">{title}</p>}
          <div className="text-sm leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function GuideSteps({ 
  children 
}: { 
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-6 mb-8 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center text-brand-600">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        </div>
        <h2 className="font-display text-xl font-bold text-brand-950">Step-by-Step Guide</h2>
      </div>
      <div className="space-y-6">
        {children}
      </div>
    </div>
  );
}

export function GuideStep({ 
  number, 
  title, 
  children 
}: { 
  number: number; 
  title: string; 
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0">
        <div className="w-8 h-8 bg-brand-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
          {number}
        </div>
      </div>
      <div className="flex-grow pt-0.5">
        <h3 className="font-display font-semibold text-brand-950 mb-2">{title}</h3>
        <div className="text-surface-700 text-sm leading-relaxed">
          {children}
        </div>
      </div>
    </div>
  );
}

export function GuideGrid({ 
  children,
  cols = 2
}: { 
  children: React.ReactNode;
  cols?: 2 | 3 | 4;
}) {
  const gridCols = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid gap-4 mb-6 ${gridCols[cols]}`}>
      {children}
    </div>
  );
}

export function GuideCard({ 
  icon,
  title, 
  children,
  variant = 'default'
}: { 
  icon?: React.ReactNode;
  title: string; 
  children: React.ReactNode;
  variant?: 'default' | 'dark' | 'accent';
}) {
  const variants = {
    default: 'bg-surface-50 border-surface-200',
    dark: 'bg-brand-950 border-brand-800 text-white',
    accent: 'bg-accent-50 border-accent-200',
  };

  const titleColors = {
    default: 'text-brand-950',
    dark: 'text-white',
    accent: 'text-accent-700',
  };

  const textColors = {
    default: 'text-surface-600',
    dark: 'text-brand-300',
    accent: 'text-accent-600',
  };

  return (
    <div className={`rounded-xl border p-5 ${variants[variant]}`}>
      {icon && (
        <div className={`mb-3 ${variant === 'dark' ? 'text-accent-400' : 'text-success-500'}`}>
          {icon}
        </div>
      )}
      <h4 className={`font-semibold mb-1 ${titleColors[variant]}`}>{title}</h4>
      <p className={`text-sm ${textColors[variant]}`}>{children}</p>
    </div>
  );
}

export function GuideDarkSection({ 
  icon,
  title, 
  children 
}: { 
  icon?: React.ReactNode;
  title: string; 
  children: React.ReactNode;
}) {
  return (
    <div className="bg-brand-950 rounded-2xl p-6 mb-8">
      <div className="flex items-center gap-3 mb-5">
        {icon && (
          <div className="w-10 h-10 bg-brand-800 rounded-xl flex items-center justify-center text-brand-300">
            {icon}
          </div>
        )}
        <h2 className="font-display text-xl font-bold text-white">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export function SeverityLevel({ 
  level, 
  badge, 
  children 
}: { 
  level: 'critical' | 'high' | 'medium' | 'low' | 'enhancement';
  badge: string;
  children: React.ReactNode;
}) {
  const configs = {
    critical: {
      bg: 'bg-error-500/10',
      border: 'border-l-error-500',
      icon: 'text-error-500',
      badge: 'bg-error-500 text-white',
      title: 'text-error-700',
    },
    high: {
      bg: 'bg-warning-500/10',
      border: 'border-l-warning-500',
      icon: 'text-warning-500',
      badge: 'bg-warning-500 text-brand-950',
      title: 'text-warning-700',
    },
    medium: {
      bg: 'bg-yellow-50',
      border: 'border-l-yellow-400',
      icon: 'text-yellow-500',
      badge: 'bg-yellow-400 text-brand-950',
      title: 'text-yellow-700',
    },
    low: {
      bg: 'bg-success-500/10',
      border: 'border-l-success-500',
      icon: 'text-success-500',
      badge: 'bg-success-500 text-white',
      title: 'text-success-700',
    },
    enhancement: {
      bg: 'bg-purple-50',
      border: 'border-l-purple-400',
      icon: 'text-purple-500',
      badge: 'bg-purple-400 text-white',
      title: 'text-purple-700',
    },
  };

  const config = configs[level];
  const icons = {
    critical: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    high: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    medium: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    low: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    enhancement: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  };

  return (
    <div className={`${config.bg} border-l-4 ${config.border} rounded-r-xl p-4 mb-3`}>
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 mt-0.5 ${config.icon}`}>
          {icons[level]}
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-semibold ${config.title} capitalize`}>{level}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.badge}`}>
              {badge}
            </span>
          </div>
          <p className="text-surface-700 text-sm">{children}</p>
        </div>
      </div>
    </div>
  );
}
